import { LogOut } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@reflex/ui/components/ui/alert';
import { Button } from '@reflex/ui/components/ui/button';
import { useAuth } from '@/hooks/auth/useAuth';

/**
 * Shown when the active org's stored Runloop key is missing / revoked but
 * the current user can't manage the org, so they can't re-enter a working
 * key themselves. Managers are redirected into the wizard's API-keys step
 * instead (see {@link resolveSandboxGateDecision}). A `trial` key is valid
 * (just devbox-limited) and never routes here — it falls through to the app.
 */
export function KeyInvalidScreen({ orgName }: { orgName: string }) {
  const { logout } = useAuth();

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <Alert className="max-w-md">
        <AlertTitle>Sandbox provider key needs attention</AlertTitle>
        <AlertDescription className="space-y-4">
          <p>
            <strong>{orgName}</strong>'s Runloop API key is missing or no longer valid, so this
            workspace can't provision sandboxes. An organization admin needs to re-enter a working
            key in the setup wizard before this workspace becomes available.
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              void logout();
            }}
            data-testid="key-invalid-screen-sign-out"
          >
            <LogOut className="icon-md" aria-hidden="true" />
            Sign out
          </Button>
        </AlertDescription>
      </Alert>
    </div>
  );
}
